import { useQuery, useMutation, useQueryClient } from "react-query";
import { API_BASE_URL } from "../config";
import { useAlert } from "../contexts/AlertProvider";

const NOTIFICATIONS_QUERY_KEY = "notifications";
const NOTIFICATIONS_REFETCH_INTERVAL_MS = 30000;

const EMPTY_RESULT = { notifications: [], unreadCount: 0, totalCount: 0 };

export const NOTIFICATIONS_PAGE_SIZE = 20;

async function fetchNotifications(agentId, size) {
  const response = await fetch(
    `${API_BASE_URL}/notifications/agent/${agentId}/v1?page=0&size=${size}`
  );
  const result = await response.json();

  if (result.status === "FAILURE") {
    throw new Error(result.failureReason || "Unable to retrieve notifications");
  }

  return result.data || EMPTY_RESULT;
}

async function markNotificationRead(agentId, notificationId) {
  const response = await fetch(
    `${API_BASE_URL}/notifications/agent/${agentId}/read/${notificationId}/v1`,
    { method: "POST" }
  );

  if (!response.ok) {
    throw new Error("Unable to mark notification as read");
  }
}

async function markAllNotificationsRead(agentId) {
  const response = await fetch(
    `${API_BASE_URL}/notifications/agent/${agentId}/read-all/v1`,
    { method: "POST" }
  );

  if (!response.ok) {
    throw new Error("Unable to mark notifications as read");
  }
}

function useNotifications(agentId, size = NOTIFICATIONS_PAGE_SIZE) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();

  const { data, isLoading } = useQuery(
    [NOTIFICATIONS_QUERY_KEY, agentId, size],
    () => fetchNotifications(agentId, size),
    {
      enabled: Boolean(agentId),
      keepPreviousData: true,
      refetchInterval: NOTIFICATIONS_REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: false,
    }
  );

  const invalidate = () =>
    queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY, agentId]);

  const onMarkReadError = () =>
    alert({
      title: "Notification Error",
      detail: "Unable to mark notification(s) as read at the moment",
      severity: "error",
      duration: 5000,
    });

  // Optimistic: the row reads as read and the badge drops straight away; a
  // failed save restores the cached lists.
  const { mutate: markRead } = useMutation(
    (notificationId) => markNotificationRead(agentId, notificationId),
    {
      onMutate: async (notificationId) => {
        await queryClient.cancelQueries([NOTIFICATIONS_QUERY_KEY, agentId]);
        const previous = queryClient.getQueriesData([NOTIFICATIONS_QUERY_KEY, agentId]);
        queryClient.setQueriesData([NOTIFICATIONS_QUERY_KEY, agentId], (cached) => {
          if (!cached) return cached;
          const target = cached.notifications?.find((n) => n.id === notificationId);
          if (!target || target.readBy?.includes(agentId)) return cached;
          return {
            ...cached,
            unreadCount: Math.max(0, (cached.unreadCount || 0) - 1),
            notifications: cached.notifications.map((n) =>
              n.id === notificationId ? { ...n, readBy: [...(n.readBy || []), agentId] } : n
            ),
          };
        });
        return { previous };
      },
      onError: (_error, _id, context) => {
        context?.previous?.forEach(([key, value]) => queryClient.setQueryData(key, value));
        onMarkReadError();
      },
      onSuccess: invalidate,
    }
  );

  const { mutate: markAllRead } = useMutation(
    () => markAllNotificationsRead(agentId),
    { onSuccess: invalidate, onError: onMarkReadError }
  );

  const result = data || EMPTY_RESULT;

  return {
    notifications: result.notifications,
    unreadCount: result.unreadCount,
    totalCount: result.totalCount,
    isLoading,
    markRead,
    markAllRead,
  };
}

export default useNotifications;
